<template>
    <div :class="{'job-list-item': isKanbanView}">
        <div class="py-2 border-bottom border-gray-100 d-flex justify-content-between status-header" :class="{'px-4': isListView, 'px-12': isKanbanView}"
            @click="toggleCollapse">
            <div class="d-flex align-items-center gap-12">
                <img src="/img/review/icons/chevron-down.svg" :class="['chevron-icon', { 'is-open': isOpen, 'd-none': isKanbanView }]"
                    width="20" alt="chevron-down" />
                <div class="icon flex-shrink-0 ml-0">
                    <img :src="statusMeta.icon" class="d-block" width="14" alt="status-icon">
                </div>
                <div class="d-flex align-items-center gap-1 ml-0">
                    <h6 class="mb-0 fw-normal text-gray-900 fs-14">{{ statusMeta.title }}</h6>
                    <span class="fs-14 text-gray-500 d-block">{{ jobs.length }}</span>
                </div>
            </div>

            <button
                class="add-btn btn border border-gray-200 p-0 rounded-circle box-32 d-flex align-items-center justify-content-center border-dashed"
                @click="$emit('open-job-modal', status)" @click.stop>
                <img src="/img/plus-light.svg" class="d-block" width="20" alt="open-job-modal-icon">
            </button>
        </div>

        <div class="collapse-jobs" :class="{ 'show': isOpen, 'py-12 max-h-fit': isKanbanView}">
            <draggable v-model="draggableJobs" group="jobs"
                @start="onDragStart"
                @end="onDragEnd"
                class="d-flex flex-column mb-0 list-unstyled drop-zone"
                :class="{ 'drop-zone-empty': draggableJobs.length === 0, 'is-dragging': isDragging, 'rounded-lg gap-2': isKanbanView }"
                :data-status="status"
                :touch-start-threshold="isMobile ? 5 : 0" 
                :force-fallback="false" 
                :fallback-tolerance="0"
                :delay="isMobile ? 200 : 0" 
                :delay-on-touch-start="false"
                :swap-threshold="0.65" 
                :invert-swap="false" 
                :animation="150"
                ghostClass="job-ghost"
                chosenClass="job-chosen"
                dragClass="job-dragging"
                :disabled="false" 
                :scroll="true" 
                :scroll-sensitivity="100"
                :bubble-scroll="true" 
                :prevent-on-filter="false" 
                :filter="'.no-drag'">
                <li v-for="job in draggableJobs" :key="job.id"
                    class="border-bottom border-opacity-10 d-flex gap-2 justify-content-between job-item bg-white"
                    :class="{'rounded-lg border p-12 kanban-card': isKanbanView, 'align-items-center px-4 py-14': isListView}"
                    @click="$emit('view-job', job)">
                    <div class="flex-fill d-flex flex-wrap align-items-center justify-content-between gap-24" :class="{'pl-3 ml-3 gap-2': isListView, 'flex-column': isKanbanView}">
                        <div class="d-flex gap-1" :class="{'flex-column flex-column-reverse align-self-start flex': isKanbanView, 'align-items-center': isListView}">
                            <span class="fs-14 text-gray-500 d-block">{{ job.jobNumber }}</span>
                            <div class="d-flex align-items-center gap-2 flex-wrap">
                                <h6 class="mb-0 fw-normal text-gray-900 fs-14">{{ job.name }}</h6>
                                <JobActivityBadges :new-activity="job.newActivity" />
                            </div>
                        </div>

                        <div class="d-flex align-items-center flex-wrap toolbar-list no-drag" :class="{'align-self-start': isKanbanView}" draggable="false">
                            <!-- Status Dropdown -->
                            <div class="dropdown dropdown-wrapper-js my-1" :class="{'ml-2': isListView}" draggable="false">
                                <button
                                    v-b-tooltip.hover.top="'Status'"
                                    class="btn btn-light fs-12 px-2 py-1 fw-normal border-gray-100 d-flex align-items-center gap-1 shadow-none lh-1 dropdown-toggle-js rounded"
                                    @click.stop="toggleStatusDropdown(job.id)">
                                    <img :src="getJobStatusIcon(job.status)" class="flex-shrink-0" width="14"
                                        alt="status-icon">
                                    {{ getJobStatusLabel(job.status) }}
                                </button>
                                <div class="dropdown-menu fh rounded-3 mt-1 py-0 border-gray-100 shadow-sm overflow-hidden dropdown-js"
                                    :class="{ 'show': openStatusDropdownJobId === job.id }" @click.stop>
                                    <ul class="list-unstyled mb-0 fs-14 list-group list-group-flush w-224">
                                        <li class="list-group-item p-0 bg-white">
                                            <span
                                                class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3 border-bottom"
                                                v-for="status in jobStatuses" :key="status.id"
                                                @click.prevent="updateJobStatus(job, status.id); closeStatusDropdown()">
                                                <img :src="getJobStatusIcon(status.id)" width="20"
                                                    class="flex-shrink-0 d-block" alt="status-icon">
                                                {{ status.name }}
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <!-- Priority Dropdown -->
                            <div class="dropdown ml-2 dropdown-wrapper-js my-1" draggable="false">
                                <button
                                    v-b-tooltip.hover.top="'Priority'"
                                    @click.stop="togglePriorityDropdown(job.id)"
                                    class="btn btn-light fs-12 px-1 py-1 fw-normal border-gray-100 d-flex align-items-center gap-1 shadow-none dropdown-toggle-js rounded">
                                    <img :src="JOB_PRIORITY_META[job.priority].icon" class="flex-shrink-0" width="14" height="14"
                                        alt="priority-icon">
                                </button>
                                <div class="dropdown-menu rounded-3 mt-1 py-0 border-gray-100 shadow-sm overflow-hidden dropdown-js"
                                    :class="{ 'show': openPriorityDropdownJobId === job.id }" @click.stop>
                                    <ul class="list-unstyled mb-0 fs-14 list-group list-group-flush w-224">
                                        <li class="list-group-item p-0 bg-white">
                                            <span v-for="(meta, priority) in JOB_PRIORITY_META" :key="priority"
                                                @click.prevent="updateJobPriority(job, Number(priority)); closePriorityDropdown()"
                                                class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3 border-bottom">
                                                <img :src="meta.icon" width="20" class="flex-shrink-0 d-block rounded-0"
                                                    alt="priority-icon">
                                                {{ meta.label }}
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <!-- Start Date -->
                            <DatePickerButton class="ml-2" :value="getJobStartDateValue(job.id)"
                                :is-open="openStartDatePickers[job.id] || false" icon="/img/calendar-time.svg"
                                tooltip="Start Date" :date-type=JOB_DATE_TYPE.START
                                :other-date-value="getJobEndDateValue(job.id)"
                                :other-date-type=JOB_DATE_TYPE.END
                                @input="handleDateSelect(job.id, CALENDAR_TYPE.START_DATE, $event)"
                                @open="handleDatePickerOpen(job.id, JOB_DATE_TYPE.START)"
                                @close="handleDatePickerClose(job.id, JOB_DATE_TYPE.START)" />

                            <!-- End Date -->
                            <DatePickerButton class="ml-2" :value="getJobEndDateValue(job.id)"
                                :is-open="openEndDatePickers[job.id] || false" icon="/img/calendar-check.svg"
                                tooltip="End Date" :date-type=JOB_DATE_TYPE.END
                                :other-date-value="getJobStartDateValue(job.id)"
                                :other-date-type=JOB_DATE_TYPE.START
                                @input="handleDateSelect(job.id, CALENDAR_TYPE.END_DATE, $event)"
                                @open="handleDatePickerOpen(job.id, JOB_DATE_TYPE.END)"
                                @close="handleDatePickerClose(job.id, JOB_DATE_TYPE.END)" />

                            <!-- Assignee Dropdown -->
                            <div class="dropdown ml-2 dropdown-wrapper-js my-1" draggable="false">
                                <button
                                    v-b-tooltip.hover.top="{ title: 'Assignee', disabled: getJobAssigneeIds(job).length > 0 }"
                                    class="btn fs-12 fw-normal d-flex align-items-center gap-1 shadow-none dropdown-toggle-js rounded"
                                    :class="getJobAssigneeIds(job).length ? 'border-none bg-transparent px-0 py-0' : 'border-gray-100 px-1 py-1'"
                                    @click.stop="toggleAssigneeDropdown(job.id)">
                                    <JobAssigneeAvatars :assignees="getJobAssigneeIds(job)" :users="accountUsers" />
                                </button>
                                <div class="dropdown-menu fh rounded-3 mt-1 py-0 border-gray-100 shadow-sm overflow-hidden dropdown-js"
                                    :class="{ 'show': openAssigneeDropdownJobId === job.id }" @click.stop>
                                    <ul class="list-unstyled mb-0 fs-14 list-group list-group-flush w-224">
                                        <li v-for="user in accountUsers" :key="user.id"
                                            class="list-group-item p-0 bg-white d-flex rounded-0">
                                            <span
                                                class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3 w-100"
                                                :class="{ 'fw-semibold text-gray-900': getJobAssigneeIds(job).includes(user.id) }"
                                                @click.prevent="toggleJobAssignee(job, user.id)">
                                                <img :src="user.profileImageUrl || '/img/avatar.svg'" width="20"
                                                    height="20" class="rounded-circle flex-shrink-0"
                                                    alt="profile-image-icon">
                                                <span class="assignee-name text-truncate ml-0">{{ user.name }}</span>
                                                <img v-if="getJobAssigneeIds(job).includes(user.id)" src="/img/check.svg" width="16"
                                                    class="d-block mx-0" alt="assignee-icon">
                                            </span>
                                        </li>
                                        <li v-if="accountUsers.length === 0" class="list-group-item p-0 bg-white">
                                            <a class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3"
                                                href="#">No users</a>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <!-- Tags Dropdown -->
                            <div class="dropdown ml-2 dropdown-wrapper-js my-1" draggable="false">
                                <button
                                    v-b-tooltip.hover.top="'Tags'"
                                    class="btn btn-light fs-12 px-1 py-1 fw-normal border-gray-100 d-flex align-items-center gap-1 shadow-none dropdown-toggle-js rounded"
                                    :class="getJobTagDisplayNames(job).length ? 'py-0' : 'py-1'"
                                    @click.stop="toggleTagsDropdown(job.id)">
                                    <img src="/img/tags.svg" class="flex-shrink-0" width="14" alt="tags-icon">
                                    <p v-if="getJobTagDisplayText(job)" class="mt-0 mb-0 fs-12">{{ getJobTagDisplayText(job) }}</p>
                                </button>
                                <div data-position="left"
                                    class="dropdown-menu rounded-3 mt-1 py-0 border-gray-100 shadow-sm overflow-hidden w-280 dropdown-js"
                                    :class="{ 'show': openTagsDropdownJobId === job.id }" @click.stop>
                                    <div class="p-2 border-bottom border-gray-100">
                                        <div class="position-relative">
                                            <img src="/img/search.svg"
                                                class="search position-absolute start-0 top-50 translate-middle-y mx-2"
                                                width="20" alt="search-icon">
                                            <input type="text"
                                                class="form-control border-gray-300 fs-14 fw-normal"
                                                placeholder="Search" v-model="tagSearchQueries[job.id]"
                                                @input="handleTagSearch(job.id, $event.target.value)"
                                                @keydown="handleTagKeydown(job.id, $event)"
                                                @focus="handleTagSearchFocus(job.id)">
                                        </div>
                                    </div>
                                    <div class="tags-scrollable-container">
                                        <ul class="list-unstyled mb-0 fs-14 list-group list-group-flush">
                                            <li v-for="tag in getFilteredTagsForJob(job.id)"
                                                class="list-group-item p-0 bg-white d-flex rounded-0">
                                                <span
                                                    class="dropdown-item text-gray-700 d-flex align-items-center py-2 px-3"
                                                    @click.prevent="toggleJobTagSelection(job, tag)"
                                                    :class="{ 'fw-semibold text-gray-900': isJobTagSelected(job, tag) }">
                                                    <div class="dot"></div>
                                                    {{ tag.name }}
                                                    <img v-if="isJobTagSelected(job, tag)" src="/img/check.svg"
                                                        width="16" class="ml-auto mx-3 d-block" alt="tags-check-icon">
                                                </span>
                                            </li>
                                            <li v-if="getFilteredTagsForJob(job.id).length === 0"
                                                class="list-group-item p-0 bg-white">
                                                <span class="text-gray-700 d-flex align-items-center gap-12 py-2 px-3">
                                                    <div class="dot"></div>
                                                    {{ tagSearchQueries[job.id] ? 'No tags found' : 'No tags available'
                                                    }}
                                                </span>
                                            </li>
                                        </ul>
                                    </div>
                                    <div class="tags-footer border-top border-gray-100 p-2 bg-white">
                                        <button v-b-modal.ManageTags
                                            class="btn btn-light w-100 fs-14 fw-normal text-gray-700 d-flex align-items-center justify-content-center">
                                            Manage Tags
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Actions Dropdown -->
                    <div class="dropdown ml-0 dropdown-wrapper-js no-drag" draggable="false">
                        <button class="btn p-1 dropdown-toggle-js" @click.stop="toggleActionsDropdown(job.id)">
                            <img src="/img/dots-horizontal.svg" width="14" alt="action-icon">
                        </button>
                        <div class="dropdown-menu dropdown-js rounded-3 mt-1 py-0 border-gray-100 shadow-sm overflow-hidden"
                            :class="{ 'show': openActionsDropdownJobId === job.id }" style="min-width: 228px;"
                            @click.stop>
                            <ul class="list-unstyled mb-0 fs-14 list-group list-group-flush">
                                <li class="action-dropdown p-0 bg-white d-flex rounded-0">
                                    <span class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3"
                                        @click.prevent="$emit('view-job', job); closeActionsDropdown();">
                                        View
                                    </span>
                                </li>
                                <li class="action-dropdown p-0 bg-white d-flex rounded-0">
                                    <span class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3"
                                        @click.prevent="$emit('edit-job', job); closeActionsDropdown();">
                                        Edit
                                    </span>
                                </li>
                                <li class="action-dropdown p-0 bg-white d-flex rounded-0">
                                    <span class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3"
                                        @click.prevent="$emit('duplicate-job', job); closeActionsDropdown();">
                                        Duplicate
                                    </span>
                                </li>
                                <li v-if="!isArchivedView" class="action-dropdown p-0 bg-white d-flex rounded-0">
                                    <span class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3"
                                        @click.prevent="$emit('archive-job', job); closeActionsDropdown();">
                                        Archive
                                    </span>
                                </li>
                                <li v-else class="action-dropdown p-0 bg-white d-flex rounded-0">
                                    <span class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3"
                                        @click.prevent="$emit('unarchive-job', job); closeActionsDropdown();">
                                        Restore
                                    </span>
                                </li>
                                <li class="action-dropdown p-0 bg-white d-flex rounded-0">
                                    <span class="dropdown-item text-gray-700 d-flex align-items-center gap-12 py-2 px-3"
                                        @click.prevent="$emit('delete-job', job); closeActionsDropdown();">
                                        Delete
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </li>
            </draggable>

            <!-- Kanban Empty State -->
            <button
                v-if="isKanbanView && draggableJobs.length === 0 && !isDragging"
                type="button"
                class="kanban-empty"
                :data-status="status"
                @click="$emit('open-job-modal', status)">
                <span class="kanban-empty__glyph" aria-hidden="true">
                    <img :src="statusMeta.icon" width="18" alt="" />
                </span>
                <span class="kanban-empty__title">Nothing in {{ statusMeta.title }}</span>
                <span class="kanban-empty__hint">
                    <img src="/img/plus-light.svg" width="12" alt="" />
                    Add a job, or drag one here
                </span>
            </button>

            <!-- Add Job Button -->
            <div v-if="isListView" class="bg-white py-14 px-4 border-bottom border-opacity-10" @click="$emit('open-job-modal', status)">
                <div class="pl-3 ml-3">
                    <div class="btn p-0 text-gray-500 fw-normal d-flex align-items-center gap-2 fs-14">
                        <img src="/img/add-job.svg" alt="add-job-icon" />
                        Add Job
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable';
import DatePickerButton from './datePickerButton.vue';
import JobActivityBadges from './job-activity-badges.vue';
import JobAssigneeAvatars from './job-assignee-avatars.vue';
import { debounce } from 'lodash';

import { MIN_DATE, JOB_STATUS_META, VARIANT, TYPES_ENUM, CALENDAR_TYPE, JOB_DATE_TYPE } from '../../utils/constants';
import { handleDropdownPosition, formatTagDisplayText, createTagOnEnter, getJobAssigneeIds } from '../../utils/helpers';

const { DANGER } = VARIANT;
const { STRING } = TYPES_ENUM;

export default {
    name: 'JobColumn',
    components: {
        JobActivityBadges,
        draggable,
        DatePickerButton,
        JobAssigneeAvatars
    },
    props: {
        accountUsers: {
            type: Array,
            default: () => []
        },
        status: {
            type: Number,
            required: true
        },
        jobs: {
            type: Array,
            default: () => []
        },
        isOpen: {
            type: Boolean,
            default: true
        },
        statusMeta: {
            type: Object,
            required: true
        },
        JOB_PRIORITY_META: {
            type: Object,
            required: true
        },
        jobTags: {
            type: Array,
            default: () => []
        },
        jobDates: {
            type: Object,
            default: () => ({})
        },
        jobStatuses: {
            type: Array,
            default: () => []
        },
        isListView: {
            type: Boolean,
            default: false
        },
        isKanbanView: {
            type: Boolean,
            default: false
        },
        isArchivedView: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            draggableJobs: [],
            openStatusDropdownJobId: null,
            openPriorityDropdownJobId: null,
            openTagsDropdownJobId: null,
            openActionsDropdownJobId: null,
            openAssigneeDropdownJobId: null,
            jobUpdaters: {},
            tagSearchQueries: {},
            pendingTagAdds: {},
            pendingTagRemoves: {},
            openStartDatePickers: {},
            openEndDatePickers: {},
            JOB_STATUS_META,
            JOB_DATE_TYPE,
            CALENDAR_TYPE,
            isMobile: false,
            isDragging: false
        };
    },
    mounted() {
        window.addEventListener('resize', handleDropdownPosition);
        window.addEventListener('resize', this.detectMobile);
        this.$nextTick(() => {
            try { handleDropdownPosition(); } catch (e) { }
        });
        document.addEventListener('click', this.handleOutsideClick);
        this.detectMobile();
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleOutsideClick);
        window.removeEventListener('resize', this.detectMobile);
    },
    watch: {
        jobs: {
            immediate: true,
            handler(newJobs) {
                this.draggableJobs = [...newJobs];
            }
        }
    },
    methods: {
        async updateJobStatus(job, newStatus) {
            try {
                const updateJobRequest = {
                    jobId: job.id,
                    jobData: {
                        status: newStatus
                    }
                };
                const updater = this.getDebouncedJobUpdater(job.id);
                updater(updateJobRequest);

                job.status = newStatus;
                this.closeAllDropdowns();

                this.$nextTick(() => {
                    try { handleDropdownPosition(); } catch (e) { }
                });
            } catch (error) {
                console.error('Failed to update job status:', error);
                this.$toast('Failed to update status', { title: 'Error', variant: DANGER });
            }
        },

        async toggleJobTagSelection(job, tag) {
            // Record pending selection only; no API here
            const jobId = job.id;
            if (!Array.isArray(this.pendingTagAdds[jobId])) this.$set(this.pendingTagAdds, jobId, []);
            if (!Array.isArray(this.pendingTagRemoves[jobId])) this.$set(this.pendingTagRemoves, jobId, []);

            const baseSelected = this.getJobTagIdSet(job).has(tag.id);
            const currentlySelected = this.isJobTagSelected(job, tag);

            if (currentlySelected) {
                // mark for removal
                this.$set(this.pendingTagAdds, jobId, (this.pendingTagAdds[jobId] || []).filter(id => id !== tag.id));
                if (baseSelected && !(this.pendingTagRemoves[jobId] || []).includes(tag.id)) {
                    this.pendingTagRemoves[jobId].push(tag.id);
                }
            } else {
                // mark for add
                this.$set(this.pendingTagRemoves, jobId, (this.pendingTagRemoves[jobId] || []).filter(id => id !== tag.id));
                if (!baseSelected && !(this.pendingTagAdds[jobId] || []).includes(tag.id)) {
                    this.pendingTagAdds[jobId].push(tag.id);
                }
            }
        },

        async updateJobPriority(job, newPriority) {
            try {
                const updateJobRequest = {
                    jobId: job.id,
                    jobData: { priority: newPriority }
                };

                const updater = this.getDebouncedJobUpdater(job.id);
                updater(updateJobRequest);

                job.priority = newPriority;
                this.closeAllDropdowns();
            } catch (error) {
                console.error('Failed to update job priority:', error);
                this.$toast('Failed to update priority', { title: 'Error', variant: DANGER });
            }
        },

        async updateJobDateField(jobId, type, value) {
            try {
                const job = this.draggableJobs.find(j => j.id === jobId);
                if (!job) {
                    console.error('Job not found:', jobId);
                    return;
                }

                const updateJobRequest = {
                    jobId: jobId,
                    jobData: { [type]: value }
                };

                const updater = this.getDebouncedJobUpdater(job.id);
                updater(updateJobRequest);

            } catch (error) {
                console.error('Failed to update job dates:', error);
                this.$toast('Failed to update date', { title: 'Error', variant: DANGER });
            }
        },

        getDebouncedJobUpdater(jobId) {
            if (!this.jobUpdaters[jobId]) {
                this.jobUpdaters[jobId] = debounce(async (updateJobRequest) => {
                    try {
                        await this.$A.JobService.updateJobById(updateJobRequest);
                        this.$emit('job-updated');
                    } catch (error) {
                        console.error('Failed to update job:', error);
                        this.$toast('Failed to update job', { title: 'Error', variant: DANGER });
                    }
                }, 500);
            }
            return this.jobUpdaters[jobId];
        },

        handleDatePickerOpen(jobId, type) {
            this.closeAllDatePickers();
            this.closeAllDropdowns();
            if (type === JOB_DATE_TYPE.START) {
                this.$set(this.openStartDatePickers, jobId, true);
            } else if (type === JOB_DATE_TYPE.END) {
                this.$set(this.openEndDatePickers, jobId, true);
            }
        },

        handleDatePickerClose(jobId, type) {
            if (type === JOB_DATE_TYPE.START) {
                this.$set(this.openStartDatePickers, jobId, false);
            } else if (type === JOB_DATE_TYPE.END) {
                this.$set(this.openEndDatePickers, jobId, false);
            }
        },

        handleDateSelect(jobId, type, value) {
            if (!this.jobDates[jobId]) {
                this.$set(this.jobDates, jobId, {});
            }

            this.$set(this.jobDates[jobId], type, value);
            this.updateJobDateField(jobId, type, value);
        },

        toggleCollapse() {
            if(!this.isListView) return;
            this.$emit('toggle-collapse', this.status);
        },


        onDragStart(evt) {
            if (evt.item) {
                evt.item.style.transform = 'scale(1.05)';
                evt.item.style.zIndex = '1000';
            }
            this.isDragging = true;
        },

        onDragEnd(evt) {
            if (evt.item) {
                evt.item.style.transform = '';
                evt.item.style.zIndex = '';
            }
            this.isDragging = false;
            this.$emit('drag-end', evt);
        },

        // Dropdown management methods
        toggleStatusDropdown(jobId) {
            const willOpen = this.openStatusDropdownJobId !== jobId;
            this.closeAllDropdowns();
            this.closeAllDatePickers();
            this.openStatusDropdownJobId = willOpen ? jobId : null;
        },

        closeStatusDropdown() {
            this.openStatusDropdownJobId = null;
        },

        togglePriorityDropdown(jobId) {
            const willOpen = this.openPriorityDropdownJobId !== jobId;
            this.closeAllDropdowns();
            this.closeAllDatePickers();
            this.openPriorityDropdownJobId = willOpen ? jobId : null;
        },

        closePriorityDropdown() {
            this.openPriorityDropdownJobId = null;
        },

        toggleTagsDropdown(jobId) {
            const willOpen = this.openTagsDropdownJobId !== jobId;
            const currentlyOpenJobId = this.openTagsDropdownJobId;

            if (currentlyOpenJobId && currentlyOpenJobId !== jobId) {
                this.savePendingTagChanges(currentlyOpenJobId);
            }

            this.closeAllDropdowns();
            this.closeAllDatePickers();

            if (!willOpen && currentlyOpenJobId) {
                this.savePendingTagChanges(currentlyOpenJobId);
            }

            this.openTagsDropdownJobId = willOpen ? jobId : null;

            if (this.openTagsDropdownJobId === jobId) {
                this.$set(this.tagSearchQueries, jobId, '');
                if (!Array.isArray(this.pendingTagAdds[jobId])) this.$set(this.pendingTagAdds, jobId, []);
                if (!Array.isArray(this.pendingTagRemoves[jobId])) this.$set(this.pendingTagRemoves, jobId, []);
            }
        },

        toggleActionsDropdown(jobId) {
            const willOpen = this.openActionsDropdownJobId !== jobId;
            this.closeAllDropdowns();
            this.closeAllDatePickers();
            this.openActionsDropdownJobId = willOpen ? jobId : null;
        },

        closeActionsDropdown() {
            this.openActionsDropdownJobId = null;
        },

        toggleAssigneeDropdown(jobId) {
            const willOpen = this.openAssigneeDropdownJobId !== jobId;
            this.closeAllDropdowns();
            this.closeAllDatePickers();
            this.openAssigneeDropdownJobId = willOpen ? jobId : null;
        },

        toggleJobAssignee(job, userId) {
            try {
                const current = getJobAssigneeIds(job);
                const assignees = current.includes(userId)
                    ? current.filter(id => id !== userId)
                    : [...current, userId];
                this.$set(job, 'assignees', assignees);
                const updater = this.getDebouncedJobUpdater(job.id);
                updater({ jobId: job.id, jobData: { assignees } });
            } catch (error) {
                console.error('Failed to update job assignee:', error);
            }
        },

        closeAllDropdowns() {
            // Save pending tag changes before closing all dropdowns
            if (this.openTagsDropdownJobId) {
                this.savePendingTagChanges(this.openTagsDropdownJobId);
            }
            
            this.openStatusDropdownJobId = null;
            this.openPriorityDropdownJobId = null;
            this.openTagsDropdownJobId = null;
            this.openActionsDropdownJobId = null;
            this.openAssigneeDropdownJobId = null;
        },

        handleOutsideClick(event) {
            const clickedEl = event.target;
            if (clickedEl.closest('.mx-calendar-header') || clickedEl.closest('.mx-calendar-panel-year') || clickedEl.closest('.mx-calendar-panel-month')) {
                return;
            }
            this.closeAllDropdowns();
            this.closeAllDatePickers();
        },

        closeAllDatePickers() {
            this.openStartDatePickers = {};
            this.openEndDatePickers = {};
        },

        handleTagSearch(jobId, searchTerm) {
            this.$set(this.tagSearchQueries, jobId, searchTerm);
        },

        handleTagSearchFocus(jobId) {
            // Ensure search query exists for this job
            if (!this.tagSearchQueries[jobId]) {
                this.$set(this.tagSearchQueries, jobId, '');
            }
        },

        handleTagKeydown(jobId, event) {
            createTagOnEnter(
                event, 
                this.tagSearchQueries[jobId], 
                this.jobTags, 
                this.$A.JobTagService,
                () => this.$set(this.tagSearchQueries, jobId, ''),
                (message) => this.$toast(message, { title: 'Error', variant: 'danger' })
            );
        },

        getFilteredTagsForJob(jobId) {
            const searchTerm = this.tagSearchQueries[jobId] || '';
            if (!searchTerm.trim()) {
                return this.jobTags;
            }

            const query = searchTerm.toLowerCase().trim();
            return this.jobTags.filter(tag =>
                tag.name.toLowerCase().includes(query)
            );
        },

        closeTagsDropdown() {
            if (this.openTagsDropdownJobId) {
                this.savePendingTagChanges(this.openTagsDropdownJobId);
            }
            this.openTagsDropdownJobId = null;
        },

        getJobTagDisplayNames(job) {
            if (!job || !Array.isArray(job.jobTagLinks)) return [];

            // Collect all tag IDs from jobTagLinks
            const tagIds = job.jobTagLinks.flatMap(link => {
                if (typeof link.tags === STRING) {
                    try {
                        return JSON.parse(link.tags);
                    } catch {
                        return [];
                    }
                }
                return Array.isArray(link.tags) ? link.tags : [];
            });

            if (!tagIds.length) return [];

            // Map IDs → names
            const idToName = {};
            (this.jobTags || []).forEach(t => {
                idToName[t.id] = t.name;
            });

            return tagIds.map(id => idToName[id]).filter(Boolean);
        },

        getJobTagIdSet(job) {
            if (!job || !Array.isArray(job.jobTagLinks)) return new Set();

            // Collect all tag IDs from jobTagLinks
            const tagIds = job.jobTagLinks.flatMap(link => {
                if (typeof link.tags === STRING) {
                    try {
                        return JSON.parse(link.tags);
                    } catch {
                        return [];
                    }
                }
                return Array.isArray(link.tags) ? link.tags : [];
            });

            return new Set(tagIds.filter(Boolean));
        },

        isJobTagSelected(job, tag) {
            const ids = this.getJobTagIdSet(job);
            const jobId = job.id;
            const pendingAdds = this.pendingTagAdds[jobId] || [];
            const pendingRemoves = this.pendingTagRemoves[jobId] || [];
            const base = ids.has(tag.id);
            return (base || pendingAdds.includes(tag.id)) && !pendingRemoves.includes(tag.id);
        },

        getJobStartDateValue(jobId) {
            const job = this.draggableJobs.find(j => j.id === jobId);
            return job?.startDate || null;
        },

        getJobEndDateValue(jobId) {
            const job = this.draggableJobs.find(j => j.id === jobId);
            return job?.endDate || null;
        },

        // Helper methods for dynamic status handling
        getJobStatusIcon(statusCategoryID) {
            const status = this.jobStatuses.find(s => s.id === statusCategoryID);
            if (status && status.icon) {
                return status.icon;
            }
            return JOB_STATUS_META[status?.statusCategoryID].icon || '/img/block.svg';
        },

        getJobStatusLabel(statusCategoryID) {
            const status = this.jobStatuses.find(s => s.id === statusCategoryID);
            return status ? status.name : 'Unknown Status';
        },

        getJobAssigneeIds,

        getJobTagDisplayText(job) {
            return formatTagDisplayText(this.getJobTagDisplayNames(job));
        },

        async savePendingTagChanges(jobId) {
            try {
                const toUpdate = (this.pendingTagAdds[jobId] || []).slice();
                const toDelete = (this.pendingTagRemoves[jobId] || []).slice();

                if (toUpdate.length === 0 && toDelete.length === 0) return;

                const updateJobRequest = {
                    jobId: jobId,
                    jobData: {
                        tags: {
                            update: toUpdate,
                            delete: toDelete
                        }
                    }
                };

                const updater = this.getDebouncedJobUpdater(jobId);
                updater(updateJobRequest);
            } catch (e) {
                console.error('Failed to commit pending tags:', e);
                this.$toast('Failed to update tags', {
                    title: 'Error',
                    variant: DANGER,
                });
            } finally {
                this.$set(this.pendingTagAdds, jobId, []);
                this.$set(this.pendingTagRemoves, jobId, []);
            }
        },

        // Detect if device is mobile
        detectMobile() {
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
            const isSmallScreen = window.innerWidth <= 768;
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

            this.isMobile = isMobileDevice || (isSmallScreen && isTouchDevice);
        },
    },
};
</script>

<style scoped>
.max-h-fit {
    max-height: fit-content !important;
}

.job-list-item {
    min-width: 350px !important;
    max-width: 350px !important;
}

.add-btn {
    border-width: 2px !important;
    border-radius: 16px !important;
}

.form-control {
    padding-left: 32px;
}

.btn {
    line-height: 12px;
}

.top-50 {
    top: 50% !important; 
}

.action-dropdown {
    min-width: 280px;
    min-height: 48px;
}

.dropdown-menu {
    z-index: 1050 !important;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease, visibility 0.15s ease;
}

.dropdown-menu.show {
    opacity: 1;
    visibility: visible;
}

.dropdown-menu.fh {
    max-height: 200px !important;
    overflow-y: auto !important;
}

.chevron-icon {
    transform: rotate(-90deg);
    transition: transform 0.2s;
}

.chevron-icon.is-open {
    transform: rotate(0deg);
}

.assignee-name {
    width: 140px;
}

.w-224 {
    width: 224px !important;
}

.w-280 {
    width: 280px !important;
}

.tags-scrollable-container {
    max-height: 165px !important;
    overflow-y: auto;
    border-radius: 0px !important;
}

.tags-footer {
    position: sticky;
    bottom: 0;
    z-index: 10;
}

.status-header {
    cursor: pointer;
}

.job-item {
    cursor: pointer;
    transition: all 0.2s ease;
    border-color: #F4F4F6 !important;
    position: relative;
}

.job-item:has(.dropdown-menu.show) {
    z-index: 1100;
}

.job-item:hover {
    background-color: #f8f9fa;
    border-color: #e9ecef !important;
}

.job-item:active {
    cursor: grabbing;
}

/* Drag and drop styles */
.drop-zone {
    transition: background-color 0.2s ease, border 0.2s ease;
    position: relative;
}

.drop-zone-empty {
    position: relative;
}

/* Only show empty state when actively dragging */
.drop-zone-empty.is-dragging {
    min-height: 100px;
}

.drop-zone-empty.is-dragging::after {
    content: 'Drop job here';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #adb5bd;
    font-size: 14px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.job-ghost {
    opacity: 0.5;
    background-color: #e3f2fd;
    border: 2px dashed #019256 !important;
}

.job-chosen {
    opacity: 0.8;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    cursor: grabbing !important;
}

.job-dragging {
    opacity: 0.5;
}

/* Highlight drop zone when dragging over */
.drop-zone.sortable-ghost-active,
.drop-zone:has(.job-ghost) {
    background-color: #f0f9f4;
    border-left: 3px solid #019256;
}

.drop-zone-empty.is-dragging.sortable-ghost-active::after,
.drop-zone-empty.is-dragging:has(.job-ghost)::after {
    opacity: 1;
}

.collapse-jobs {
    max-height: 0px;
    overflow: hidden;
    transition: max-height 0.5s ease;
}

.collapse-jobs.show {
    max-height: 1000px;
    transition: max-height 0.5s ease-in-out;
}

.collapse-jobs:has(.dropdown-menu.show) {
    overflow: visible !important;
}

.collapse-jobs:has(.mx-datepicker) {
    overflow: visible !important;
}

.dot {
    width: 8px;
    height: 8px;
    background: #adb5bd;
    border-radius: 50%;
    display: inline-block;
    margin-right: 8px;
}

.ml-0 {
    margin-left: 0px !important;
}

.rounded-0 {
    border-radius: 0px !important;
}

.kanban-card {
    border-color: #E5E5E5 !important;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.kanban-card:hover {
    border-color: #D1D5DB !important;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04);
    transform: translateY(-1px);
}

/* Kanban empty-column placeholder */
.kanban-empty {
    --tint: #9CA3AF;
    --tint-bg: rgba(156, 163, 175, 0.06);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 28px 16px;
    border: 1.5px dashed #E1E3E8;
    border-radius: 10px;
    background:
        radial-gradient(120% 80% at 50% 0%, var(--tint-bg), transparent 70%),
        #FCFCFD;
    color: #6B7280;
    cursor: pointer;
    text-align: center;
    transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;
    appearance: none;
    outline: none;
}

.kanban-empty:hover {
    border-color: var(--tint);
    background:
        radial-gradient(120% 80% at 50% 0%, var(--tint-bg), transparent 65%),
        #FFFFFF;
    transform: translateY(-1px);
}

.kanban-empty:focus-visible {
    border-style: solid;
    border-color: var(--tint);
    box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}

.kanban-empty__glyph {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--tint-bg);
    border: 1px solid rgba(0, 0, 0, 0.04);
}

.kanban-empty__glyph img {
    opacity: 0.65;
    display: block;
}

.kanban-empty__title {
    font-size: 13px;
    font-weight: 500;
    color: #4B5563;
    letter-spacing: -0.1px;
}

.kanban-empty__hint {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #9CA3AF;
}

.kanban-empty__hint img {
    opacity: 0.55;
}

.kanban-empty[data-status="1"] { --tint: #9CA3AF; --tint-bg: rgba(156, 163, 175, 0.08); }
.kanban-empty[data-status="2"] { --tint: #6B7280; --tint-bg: rgba(107, 114, 128, 0.08); }
.kanban-empty[data-status="3"] { --tint: #F59E0B; --tint-bg: rgba(245, 158, 11, 0.08); }
.kanban-empty[data-status="4"] { --tint: #019256; --tint-bg: rgba(1, 146, 86, 0.07); }
.kanban-empty[data-status="5"] { --tint: #EF4444; --tint-bg: rgba(239, 68, 68, 0.07); }

.sortable-ghost {
    opacity: 0.9 !important;
    transform: rotate(3deg) scale(1.01) !important;
    box-shadow: 0 12px 35px rgba(0,0,0,0.3) !important;
    background-color: #ffffff !important;
    border-radius: 8px !important;
}

.sortable-chosen {
    transform: scale(1.01) !important;
    box-shadow: 0 8px 25px rgba(0,0,0,0.2) !important;
    z-index: 1000 !important;
    background-color: #f8f9fa !important;
    border: 1px solid #dee2e6 !important;
}

.sortable-drag {
    transform: scale(1.01) !important;
    opacity: 0 !important;
    box-shadow: 0 15px 40px rgba(0,0,0,0.35) !important;
    background-color: #ffffff !important;
}

.job-list-item .collapse-jobs.show {
    scrollbar-width: none;
}

@media (max-width: 992px) {
    .dropdown-menu {
        box-shadow: 0px 9px 28px 8px rgba(0, 0, 0, 0.05) !important;
        box-shadow: 0px 3px 6px -4px rgba(0, 0, 0, 0.12) !important;
        box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08) !important;
    }
}

@media (max-width: 768px) {
    .job-item .flex-fill {
        padding-left: 0 !important;
        margin-left: 0 !important;
    }
}
</style>
